/**
 * LoginForm Component
 * 
 * Displays WordPress login form with username/password fields.
 * Handles authentication via JWT endpoint.
 * 
 * Requirements: 4.1, 4.3
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { login } from '../api/auth';
import type { AuthError } from '../api/auth';
import { useAuth } from '../context/AuthContext';

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { login: authLogin } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const session = await login({ username, password });
      authLogin(session);
      onSuccess?.();
    } catch (err) {
      const authError = err as AuthError;
      if (authError.code === 'jwt_auth_invalid_username' || 
          authError.code === 'jwt_auth_invalid_password' ||
          authError.code === '[jwt_auth] invalid_username' ||
          authError.code === '[jwt_auth] incorrect_password') {
        setError('Invalid username or password.');
      } else if (authError.code === 'jwt_auth_bad_credentials') {
        setError('Invalid username or password.');
      } else {
        setError(authError.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-form-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h2>WKS Product Entry</h2>
        <p className="login-subtitle">Sign in with your WordPress account</p>
        
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        
        <div className="form-field">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
            required
            disabled={isLoading}
            autoComplete="username"
          />
        </div>
        
        <div className="form-field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            disabled={isLoading}
            autoComplete="current-password"
          />
        </div>
        
        <button 
          type="submit" 
          className="login-button"
          disabled={isLoading || !username || !password}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

export default LoginForm;
